import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Flex,
  Text,
  Button,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  useToast,
  Heading,
  Input,
  HStack,
  VStack,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  Badge,
  Progress,
  SimpleGrid,
} from "@chakra-ui/react";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  DownloadIcon,
} from "@chakra-ui/icons";
import { useUser } from "../../contexts/UserContext";
import { supabase } from "../../lib/supabaseClient";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

const PAGE_SIZE = 20;

export default function AdminVideoRevenue() {
  const { userInfo } = useUser();
  const [revenueData, setRevenueData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  // 수익 계산 모달 상태
  const [calculationDate, setCalculationDate] = useState(new Date().toISOString().split('T')[0]);
  const [calculating, setCalculating] = useState(false);
  const [calculationProgress, setCalculationProgress] = useState(0);
  const [calculationResults, setCalculationResults] = useState([]);
  const [currentProcessing, setCurrentProcessing] = useState("");

  // 필터 상태
  const [filters, setFilters] = useState({
    uploadAtStart: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    uploadAtEnd: new Date().toISOString().split('T')[0],
    channelName: "",
    licensedVideoTitle: "",
    contentId: "",
    company: "",
  });

  // 회사 목록
  const [companyList, setCompanyList] = useState([]);
  const [videoList, setVideoList] = useState([]);

  // 수익 요약
  const [summary, setSummary] = useState({
    total_profits_krw: 0,
    total_laeebly_revenue: 0,
    total_creator_revenue: 0,
    total_copyrights_revenue: 0,
    total_views: 0,
    total_valid_views: 0,
    total_laeebly_shopping_revenue: 0,
    total_creator_shopping_revenue: 0,
    total_copyrights_shopping_revenue: 0,
  });

  // 페이지네이션 상태
  const [totalPages, setTotalPages] = useState(1);

  // 데이터 조회
  const fetchRevenueData = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc("get_youtube_studio_summary", {
        upload_at_start: filters.uploadAtStart,
        upload_at_end: filters.uploadAtEnd,
        channel_name_param: filters.channelName || null,
        licensed_video_title_param: filters.licensedVideoTitle || null,
        content_id_param: filters.contentId || null,
        company_param: filters.company || null,
        page_number: page,
      });

      if (error) {
        console.error("수익 데이터 조회 오류:", error);
        toast({
          title: "데이터 조회 실패",
          description: error.message,
          status: "error",
        });
        setRevenueData([]);
        setTotalPages(1);
      } else {
        setRevenueData(data || []);
        
        if (data && data.length > 0 && data[0].total_pages) {
          setTotalPages(Number(data[0].total_pages));
        } else {
          setTotalPages(1);
        }
      }
    } catch (error) {
      console.error("수익 데이터 조회 오류:", error);
      toast({
        title: "데이터 조회 실패",
        description: error.message,
        status: "error",
      });
      setRevenueData([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [filters, page, toast]);

  // 요약 데이터 fetch (get_youtube_studio_totals)
  const fetchSummaryTotals = useCallback(async () => {
    try {
      const { data, error } = await supabase.rpc("get_youtube_studio_totals", {
        upload_at_start: filters.uploadAtStart,
        upload_at_end: filters.uploadAtEnd,
      });
      if (!error && data && data[0]) {
        setSummary(data[0]);
      } else {
        setSummary({
          total_profits_krw: 0,
          total_laeebly_revenue: 0,
          total_creator_revenue: 0,
          total_copyrights_revenue: 0,
          total_views: 0,
          total_valid_views: 0,
          total_laeebly_shopping_revenue: 0,
          total_creator_shopping_revenue: 0,
          total_copyrights_shopping_revenue: 0,
        });
      }
    } catch (error) {
      setSummary({
        total_profits_krw: 0,
        total_laeebly_revenue: 0,
        total_creator_revenue: 0,
        total_copyrights_revenue: 0,
        total_views: 0,
        total_valid_views: 0,
        total_laeebly_shopping_revenue: 0,
        total_creator_shopping_revenue: 0,
        total_copyrights_shopping_revenue: 0,
      });
    }
  }, [filters.uploadAtStart, filters.uploadAtEnd]);

  // 필터 옵션들 조회
  const fetchFilterOptions = useCallback(async () => {
    try {
      // 회사 목록 조회
      const { data: companies } = await supabase
        .from("content_company")
        .select("company_name")
        .order("company_name");
      setCompanyList(companies || []);

      // 영상 제목 목록 조회 (licensed_video 테이블의 title)
      const { data: videos } = await supabase
        .from("licensed_video")
        .select("title")
        .not("title", "is", null)
        .order("title");
      const uniqueVideos = [...new Set(videos?.map(v => v.title) || [])];
      setVideoList(uniqueVideos);
    } catch (error) {
      console.error("필터 옵션 조회 오류:", error);
    }
  }, []);

  // 필터 변경 핸들러
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1); // 필터 변경 시 첫 페이지로
  };

  // 날짜 변경 핸들러 (즉시 검색)
  const handleDateChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  };

  // 검색 실행
  const handleSearch = useCallback(() => {
    fetchRevenueData();
  }, [fetchRevenueData]);

  // 엑셀 다운로드
  const handleExcelDownload = async () => {
    if (revenueData.length === 0) {
      toast({
        title: "다운로드할 데이터가 없습니다.",
        status: "warning",
      });
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("영상 수익 데이터");

    // 헤더 설정
    const headers = [
      "채널명",
      "영상 제목",
      "콘텐츠명",
      "유효 조회수",
      "총 조회수",
      "업로드 날짜",
      "총 수익 (KRW)",
      "회사",
      "Laeebly 수익",
      "크리에이터 수익",
      "저작권자 수익",
      "Laeebly 쇼핑 수익",
      "크리에이터 쇼핑 수익",
      "저작권자 쇼핑 수익",
    ];

    headers.forEach((header, index) => {
      const cell = worksheet.getRow(1).getCell(index + 1);
      cell.value = header;
      cell.font = { bold: true };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFE0E0E0" },
      };
    });

    // 데이터 추가
    revenueData.forEach((item, index) => {
      const row = worksheet.getRow(index + 2);
      row.getCell(1).value = item.channel_name || "";
      row.getCell(2).value = item.video_title || "";
      row.getCell(3).value = item.licensed_video_title || "";
      row.getCell(4).value = item.total_valid_views || 0;
      row.getCell(5).value = item.total_views || 0;
      row.getCell(6).value = item.upload_at || "";
      row.getCell(7).value = item.total_profits_krw || 0;
      row.getCell(8).value = item.company || "";
      row.getCell(9).value = item.total_laeebly_revenue || 0;
      row.getCell(10).value = item.total_creator_revenue || 0;
      row.getCell(11).value = item.total_copyrights_revenue || 0;
      row.getCell(12).value = item.total_laeebly_shopping_revenue || 0;
      row.getCell(13).value = item.total_creator_shopping_revenue || 0;
      row.getCell(14).value = item.total_copyrights_shopping_revenue || 0;

      // 숫자 포맷 적용
      [4, 5, 7, 9, 10, 11, 12, 13, 14].forEach(colIndex => {
        row.getCell(colIndex).numFmt = "#,##0";
      });
    });

    // 컬럼 너비 자동 조정
    worksheet.columns.forEach(column => {
      column.width = 15;
    });

    // 파일 생성 및 다운로드
    const buffer = await workbook.xlsx.writeBuffer();
    const fileName = `영상수익_${filters.uploadAtStart}_${filters.uploadAtEnd}.xlsx`;
    saveAs(new Blob([buffer]), fileName);

    toast({
      title: "엑셀 다운로드 완료",
      status: "success",
    });
  };

  // 수익 계산 실행
  const handleRevenueCalculation = async () => {
    if (!calculationDate) {
      toast({
        title: "날짜를 선택해주세요.",
        status: "warning",
      });
      return;
    }

    setCalculating(true);
    setCalculationProgress(0);
    setCalculationResults([]);
    setCurrentProcessing("라이센스 비디오 정보 조회 중...");

    try {
      // 1. licensed_video 테이블에서 정보 조회
      const { data: licensedVideos, error: licensedError } = await supabase
        .from("licensed_video")
        .select("title, share_video_laeebly, share_video_creator, share_video_copyright, share_shopping_laeebly, share_shopping_creator, share_shopping_copyright");

      if (licensedError) {
        throw new Error(`라이센스 비디오 조회 오류: ${licensedError.message}`);
      }

      setCurrentProcessing("YouTube 스튜디오 데이터 조회 중...");
      setCalculationProgress(20);

      // 2. youtube_studio 테이블에서 해당 날짜의 데이터 조회
      const { data: youtubeData, error: youtubeError } = await supabase
        .from("youtube_studio")
        .select("*")
        .eq("upload_at", calculationDate)
        .not("licensed_video_title", "is", null);

      if (youtubeError) {
        throw new Error(`YouTube 스튜디오 데이터 조회 오류: ${youtubeError.message}`);
      }

      setCurrentProcessing("수익 계산 중...");
      setCalculationProgress(40);

      // 3. 수익 계산 및 배치 업데이트 준비
      const updateBatches = [];
      const results = [];
      let processedCount = 0;
      const totalCount = youtubeData.length;

      // 수익 계산 및 배치 준비
      for (const youtubeItem of youtubeData) {
        const licensedVideo = licensedVideos.find(lv => lv.title === youtubeItem.licensed_video_title);
        
        if (licensedVideo) {
          const profitsKrw = youtubeItem.profits_krw || 0;
          const pendingRevenue = (youtubeItem.pending_revenue - youtubeItem.returned_revenue) || 0;
          const netProfit = profitsKrw - pendingRevenue;

          // 수익 계산
          const laeeblyRevenue = netProfit * (licensedVideo.share_video_laeebly / 100);
          const creatorRevenue = netProfit * (licensedVideo.share_video_creator / 100);
          const copyrightsRevenue = netProfit * (licensedVideo.share_video_copyright / 100);
          const laeeblyShoppingRevenue = pendingRevenue * (licensedVideo.share_shopping_laeebly / 100);
          const creatorShoppingRevenue = pendingRevenue * (licensedVideo.share_shopping_creator / 100);
          const copyrightsShoppingRevenue = pendingRevenue * (licensedVideo.share_shopping_copyright / 100);

          // 업데이트 데이터 준비
          updateBatches.push({
            id: youtubeItem.id,
            laeebly_revenue: laeeblyRevenue,
            creator_revenue: creatorRevenue,
            copyrights_revenue: copyrightsRevenue,
            laeebly_shopping_revenue: laeeblyShoppingRevenue,
            creator_shopping_revenue: creatorShoppingRevenue,
            copyrights_shopping_revenue: copyrightsShoppingRevenue,
          });

          results.push({
            title: youtubeItem.licensed_video_title,
            status: "계산 완료",
            laeeblyRevenue,
            creatorRevenue,
            copyrightsRevenue,
            laeeblyShoppingRevenue,
            creatorShoppingRevenue,
            copyrightsShoppingRevenue,
          });
        } else {
          results.push({
            title: youtubeItem.licensed_video_title,
            status: "라이센스 정보 없음",
          });
        }

        processedCount++;
        setCalculationProgress(40 + (processedCount / totalCount) * 20);
        setCurrentProcessing(`수익 계산 중... (${processedCount}/${totalCount})`);
      }

      setCurrentProcessing("개별 업데이트 중...");
      setCalculationProgress(60);

      // 4. 개별 업데이트 실행 (배치 단위로 진행 상황 표시)
      let updatedCount = 0;
      const BATCH_SIZE = 100;
      for (let i = 0; i < updateBatches.length; i += BATCH_SIZE) {
        const batch = updateBatches.slice(i, i + BATCH_SIZE);
        try {
          // upsert로 100개씩 한 번에 업데이트
          const { error: updateError } = await supabase
            .from("youtube_studio")
            .upsert(batch, { onConflict: ["id"] });

          batch.forEach(item => {
            const resultIndex = results.findIndex(r => {
              const youtubeItem = youtubeData.find(y => y.id === item.id);
              return youtubeItem && r.title === youtubeItem.licensed_video_title;
            });
            if (updateError) {
              if (resultIndex !== -1) {
                results[resultIndex].status = "업데이트 실패";
                results[resultIndex].error = updateError.message;
              }
            } else {
              if (resultIndex !== -1) {
                results[resultIndex].status = "성공";
              }
            }
          });
        } catch (error) {
          batch.forEach(item => {
            const resultIndex = results.findIndex(r => {
              const youtubeItem = youtubeData.find(y => y.id === item.id);
              return youtubeItem && r.title === youtubeItem.licensed_video_title;
            });
            if (resultIndex !== -1) {
              results[resultIndex].status = "업데이트 실패";
              results[resultIndex].error = error.message;
            }
          });
        }
        updatedCount += batch.length;
        setCalculationProgress(60 + (updatedCount / updateBatches.length) * 30);
        setCurrentProcessing(`개별 업데이트 중... (${updatedCount}/${updateBatches.length})`);
      }

      setCalculationProgress(100);
      setCurrentProcessing("완료");
      setCalculationResults(results);

      const successCount = results.filter(r => r.status === "성공").length;
      const failCount = results.filter(r => r.status === "업데이트 실패").length;
      const noLicenseCount = results.filter(r => r.status === "라이센스 정보 없음").length;

      toast({
        title: "수익 계산 완료",
        description: `성공: ${successCount}개, 실패: ${failCount}개, 라이센스 정보 없음: ${noLicenseCount}개`,
        status: successCount > 0 ? "success" : "warning",
      });

    } catch (error) {
      console.error("수익 계산 오류:", error);
      toast({
        title: "수익 계산 실패",
        description: error.message,
        status: "error",
      });
    } finally {
      setCalculating(false);
    }
  };

  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  useEffect(() => {
    fetchRevenueData();
  }, [fetchRevenueData]);

  useEffect(() => {
    fetchSummaryTotals();
  }, [fetchSummaryTotals]);

  return (
    <Box minH="100vh" bg="#f7fafd" p={6}>
      {/* 상단 요약 카드 및 버튼 */}
      <Box bg="white" borderRadius="2xl" p={6} boxShadow="md" mb={6}>
        <Flex justify="space-between" align="center" mb={4}>
          <Text fontWeight="bold" fontSize="xl">영상 수익 요약</Text>
          <HStack>
            <Text color="gray.400" fontWeight="bold">기간</Text>
            <Input
              type="date"
              value={filters.uploadAtStart}
              onChange={e => handleDateChange("uploadAtStart", e.target.value)}
              w="160px"
              max={filters.uploadAtEnd}
            />
            <Text color="gray.500">~</Text>
            <Input
              type="date"
              value={filters.uploadAtEnd}
              onChange={e => handleDateChange("uploadAtEnd", e.target.value)}
              w="160px"
              min={filters.uploadAtStart}
              max={new Date().toISOString().split('T')[0]}
            />
            <Button
              leftIcon={<DownloadIcon />}
              colorScheme="green"
              isDisabled={revenueData.length === 0}
              onClick={handleExcelDownload}
            >
              엑셀 다운로드
            </Button>
            <Button colorScheme="purple" onClick={onOpen}>
              수익 계산
            </Button>
          </HStack>
        </Flex>
        {/* 요약 정보 */}
        <SimpleGrid columns={{ base: 2, md: 7 }} spacing={4}>
          <Box><Text color="gray.500" fontSize="sm">총 수익</Text><Text fontWeight="bold" color="green.500">{Number(summary.total_profits_krw || 0).toLocaleString()}원</Text></Box>
          <Box><Text color="gray.500" fontSize="sm">Laeebly 수익</Text><Text fontWeight="bold" color="blue.500">{Number(summary.total_laeebly_revenue || 0).toLocaleString()}원</Text></Box>
          <Box><Text color="gray.500" fontSize="sm">크리에이터 수익</Text><Text fontWeight="bold" color="purple.500">{Number(summary.total_creator_revenue || 0).toLocaleString()}원</Text></Box>
          <Box><Text color="gray.500" fontSize="sm">저작권자 수익</Text><Text fontWeight="bold" color="orange.500">{Number(summary.total_copyrights_revenue || 0).toLocaleString()}원</Text></Box>
          <Box><Text color="gray.500" fontSize="sm">Laeebly 쇼핑 수익</Text><Text fontWeight="bold" color="blue.400">{Number(summary.total_laeebly_shopping_revenue || 0).toLocaleString()}원</Text></Box>
          <Box><Text color="gray.500" fontSize="sm">크리에이터 쇼핑 수익</Text><Text fontWeight="bold" color="purple.400">{Number(summary.total_creator_shopping_revenue || 0).toLocaleString()}원</Text></Box>
          <Box><Text color="gray.500" fontSize="sm">저작권자 쇼핑 수익</Text><Text fontWeight="bold" color="orange.400">{Number(summary.total_copyrights_shopping_revenue || 0).toLocaleString()}원</Text></Box>
        </SimpleGrid>
        <SimpleGrid columns={{ base: 2, md: 2 }} spacing={4} mt={4}>
          <Box><Text color="gray.500" fontSize="sm">총 조회수</Text><Text fontWeight="bold">{Number(summary.total_views || 0).toLocaleString()}회</Text></Box>
          <Box><Text color="gray.500" fontSize="sm">유효 조회수</Text><Text fontWeight="bold">{Number(summary.total_valid_views || 0).toLocaleString()}회</Text></Box>
        </SimpleGrid>
      </Box>

      {/* 검색/필터 영역 */}
      <Box bg="white" borderRadius="xl" p={4} boxShadow="sm" mb={6}>
        <Flex justify="space-between" align="center" flexWrap="wrap" gap={4}>
          <HStack spacing={6} flexWrap="wrap">
            <Box>
              <Text fontSize="sm" fontWeight="bold" mb={1}>채널명</Text>
              <Input
                value={filters.channelName}
                onChange={e => handleFilterChange("channelName", e.target.value)}
                placeholder="채널명 입력"
                w="200px"
              />
            </Box>
            <Box>
              <Text fontSize="sm" fontWeight="bold" mb={1}>라이센스 영상 제목</Text>
              <Select
                value={filters.licensedVideoTitle}
                onChange={e => handleFilterChange("licensedVideoTitle", e.target.value)}
                placeholder="전체"
                w="200px"
              >
                {videoList.map((video) => (
                  <option key={video} value={video}>{video}</option>
                ))}
              </Select>
            </Box>
            <Box>
              <Text fontSize="sm" fontWeight="bold" mb={1}>콘텐츠 ID</Text>
              <Input
                value={filters.contentId}
                onChange={e => handleFilterChange("contentId", e.target.value)}
                placeholder="콘텐츠 ID 입력"
                w="200px"
              />
            </Box>
            <Box>
              <Text fontSize="sm" fontWeight="bold" mb={1}>회사</Text>
              <Select
                value={filters.company}
                onChange={e => handleFilterChange("company", e.target.value)}
                placeholder="전체"
                w="200px"
              >
                {companyList.map((company) => (
                  <option key={company.company_name} value={company.company_name}>{company.company_name}</option>
                ))}
              </Select>
            </Box>
          </HStack>
          <Button colorScheme="blue" onClick={handleSearch} isLoading={loading} minW="120px" h="40px">검색</Button>
        </Flex>
      </Box>

      {/* 데이터 테이블 */}
      <Box bg="white" borderRadius="2xl" p={4} boxShadow="md">
        {loading ? (
          <Flex justify="center" align="center" minH="200px">
            <Spinner />
          </Flex>
        ) : (
          <Table size="sm" variant="simple">
            <Thead>
              <Tr>
                <Th>채널명</Th>
                <Th>영상 제목</Th>
                <Th>콘텐츠명</Th>
                <Th>업로드 날짜</Th>
                <Th>유효 조회수</Th>
                <Th>총 조회수</Th>
                <Th>총 수익 (KRW)</Th>
                <Th>레이블리수익</Th>
                <Th>사용자수익</Th>
                <Th>저작권자수익</Th>
                <Th>Laeebly쇼핑수익</Th>
                <Th>사용자쇼핑수익</Th>
                <Th>저작권자쇼핑수익</Th>
                <Th>회사</Th>
              </Tr>
            </Thead>
            <Tbody>
              {revenueData.length === 0 ? (
                <Tr><Td colSpan={14} textAlign="center" color="gray.400">데이터 없음</Td></Tr>
              ) : (
                revenueData.map((item, index) => (
                  <Tr key={index}>
                    <Td fontSize="sm">{item.channel_name || "-"}</Td>
                    <Td fontSize="sm" maxW="200px" isTruncated>{item.video_title || "-"}</Td>
                    <Td fontSize="sm" maxW="200px" isTruncated>{item.licensed_video_title || "-"}</Td>
                    <Td fontSize="sm">{item.upload_at || "-"}</Td>
                    <Td fontSize="sm">{item.total_valid_views?.toLocaleString() || "0"}</Td>
                    <Td fontSize="sm">{item.total_views?.toLocaleString() || "0"}</Td>
                    <Td fontSize="sm" fontWeight="bold" color="green.500">{item.total_profits_krw?.toLocaleString() || "0"}원</Td>
                    <Td fontSize="sm" color="blue.500">{item.total_laeebly_revenue?.toLocaleString() || "0"}원</Td>
                    <Td fontSize="sm" color="purple.500">{item.total_creator_revenue || "0"}원</Td>
                    <Td fontSize="sm" color="orange.500">{item.total_copyrights_revenue?.toLocaleString() || "0"}원</Td>
                    <Td fontSize="sm" color="blue.400">{item.total_laeebly_shopping_revenue?.toLocaleString() || "0"}원</Td>
                    <Td fontSize="sm" color="purple.400">{item.total_creator_shopping_revenue?.toLocaleString() || "0"}원</Td>
                    <Td fontSize="sm" color="orange.400">{item.total_copyrights_shopping_revenue?.toLocaleString() || "0"}원</Td>
                    <Td fontSize="sm">{item.company || "-"}</Td>
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        )}
        {/* 페이지네이션 */}
        <Flex justify="center" align="center" mt={4} gap={2}>
          <Button size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} isDisabled={page === 1}>이전</Button>
          <Text fontWeight="bold" fontSize="sm">{page} / {totalPages}</Text>
          <Button size="sm" onClick={() => setPage(p => p + 1)} isDisabled={page >= totalPages}>다음</Button>
        </Flex>
      </Box>

      {/* 수익 계산 모달 */}
      <Modal isOpen={isOpen} onClose={onClose} size="6xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>수익 계산</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={6} align="stretch">
              <Box>
                <Text fontSize="sm" fontWeight="bold" mb={2}>업로드 날짜</Text>
                <Input
                  type="date"
                  value={calculationDate}
                  onChange={(e) => setCalculationDate(e.target.value)}
                  isDisabled={calculating}
                />
              </Box>

              {calculating && (
                <Box>
                  <Text fontSize="sm" fontWeight="bold" mb={2}>진행 상황</Text>
                  <Progress value={calculationProgress} colorScheme="blue" mb={2} />
                  <Text fontSize="sm" color="gray.600">{currentProcessing}</Text>
                </Box>
              )}

              {calculationResults.length > 0 && (
                <Box>
                  <Text fontSize="sm" fontWeight="bold" mb={4}>처리 결과</Text>
                  <Box maxH="400px" overflowY="auto">
                    <Table variant="simple" size="sm">
                      <Thead>
                        <Tr>
                          <Th>제목</Th>
                          <Th>상태</Th>
                          <Th>Laeebly 수익</Th>
                          <Th>크리에이터 수익</Th>
                          <Th>저작권자 수익</Th>
                          <Th>Laeebly 쇼핑</Th>
                          <Th>크리에이터 쇼핑</Th>
                          <Th>저작권자 쇼핑</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {calculationResults.map((result, index) => (
                          <Tr key={index}>
                            <Td fontSize="sm" maxW="200px" isTruncated>
                              {result.title}
                            </Td>
                            <Td>
                              <Badge
                                colorScheme={result.status === "성공" ? "green" : result.status === "실패" ? "red" : "yellow"}
                              >
                                {result.status}
                              </Badge>
                            </Td>
                            <Td fontSize="sm">
                              {result.laeeblyRevenue?.toLocaleString() || "-"}
                            </Td>
                            <Td fontSize="sm">
                              {result.creatorRevenue?.toLocaleString() || "-"}
                            </Td>
                            <Td fontSize="sm">
                              {result.copyrightsRevenue?.toLocaleString() || "-"}
                            </Td>
                            <Td fontSize="sm">
                              {result.laeeblyShoppingRevenue?.toLocaleString() || "-"}
                            </Td>
                            <Td fontSize="sm">
                              {result.creatorShoppingRevenue?.toLocaleString() || "-"}
                            </Td>
                            <Td fontSize="sm">
                              {result.copyrightsShoppingRevenue?.toLocaleString() || "-"}
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                </Box>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" onClick={handleRevenueCalculation} isLoading={calculating} isDisabled={!calculationDate}>
              계산하기
            </Button>
            <Button ml={3} onClick={onClose}>
              닫기
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
} 